import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DataTable, DataTablePagination } from '@/components/ui/data-table';
import { Users, Search, X, Pencil, Plus, Trash2, Shield, Archive, RotateCcw } from 'lucide-react';
import { useAuth } from '@/lib/contexts/auth-context';
import { getRoleLabel, getRoleBadgeClass, getUserStatusLabel, getUserStatusBadgeClass } from '@/lib/utils/utils';
import { useTranslation } from 'react-i18next';

export default function CollaboratorsDataTable({
    collaborators,
    onInvite,
    onEdit,
    canEditPermissions,
    onDelete,
    onReactivate,
    height,
    // Server-side pagination props (optional)
    manualPagination = false,
    pageCount,
    currentPage,
    onPageChange,
    onPageSizeChange,
    pageSize,
    totalCount,
    isLoading = false,
    // Server-side search props (optional)
    searchValue,
    onSearchChange,
}) {
    const { t } = useTranslation();
    const [localSearch, setLocalSearch] = useState('');
    const { user } = useAuth();
    const navigate = useNavigate();

    const search = manualPagination ? (searchValue ?? '') : localSearch;
    const setSearch = manualPagination ? (onSearchChange ?? (() => {})) : setLocalSearch;

    const filteredCollaborators = manualPagination
        ? collaborators
        : collaborators.filter((c) => {
              if (!search.trim()) return true;
              const s = search.toLowerCase();
              return c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s);
          });

    const displayCount = manualPagination && totalCount !== undefined ? totalCount : collaborators.length;

    // Déterminer si les actions (edit/delete/invite) sont disponibles selon les permissions
    const canInvite = typeof onInvite === 'function';
    const canEdit = typeof onEdit === 'function';
    const canDelete = typeof onDelete === 'function';
    const canReactivate = typeof onReactivate === 'function';
    const hasActions = canEdit || canDelete || canReactivate;

    const columns = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: t('common:nom'),
                cell: ({ row }) => <span className="text-[13px] text-foreground">{row.original.name || '—'}</span>,
                meta: { hideOnMobile: true, className: 'w-[30%]' },
            },
            {
                accessorKey: 'role',
                header: t('common:roleLabel'),
                cell: ({ row }) => {
                    const role = row.original.role;
                    if (!role) return null;
                    const roleName = typeof role === 'string' ? role : role.name;
                    const displayName =
                        typeof role === 'string' ? getRoleLabel(role) : (role.display_name ?? getRoleLabel(roleName));
                    return (
                        <span
                            className={`inline-flex items-center px-2 py-[3px] rounded-md text-[12px] ${getRoleBadgeClass(roleName)}`}
                        >
                            {displayName}
                        </span>
                    );
                },
                meta: { hideOnMobile: true, className: 'w-[20%]' },
            },
            {
                accessorKey: 'email',
                header: t('common:email'),
                cell: ({ row }) => <span className="text-[13px] text-foreground truncate">{row.original.email}</span>,
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'status',
                header: t('common:statut'),
                cell: ({ row }) => {
                    const status = row.original.status;
                    if (!status) return null;
                    const isArchived = status === 'Compte archivé' || status === 'ARCHIVED';
                    return (
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[12px] ${getUserStatusBadgeClass(status)}`}
                        >
                            {isArchived && <Archive className="w-3 h-3" />}
                            {getUserStatusLabel(status)}
                        </span>
                    );
                },
                meta: { hideOnMobile: true, className: 'w-[20%]' },
            },
            ...(hasActions
                ? [
                      {
                          id: 'actions',
                          header: () => <span className="text-right block">{t('common:actions')}</span>,
                          cell: ({ row }) => {
                              const c = row.original;
                              const isCurrentUser = user && c.email === user.email;
                              if (isCurrentUser) return null;
                              const isArchived = c.status === 'Compte archivé' || c.status === 'ARCHIVED';
                              return (
                                  <div className="flex items-center justify-end gap-1">
                                      {isArchived && canReactivate ? (
                                          <Button
                                              variant="plain"
                                              color="neutral"
                                              size="auto"
                                              className="w-8 h-8 rounded-full hover:bg-border"
                                              onClick={() => onReactivate(c)}
                                              title={t('common:restaurer')}
                                          >
                                              <RotateCcw className="w-4 h-4 text-muted-foreground" />
                                          </Button>
                                      ) : !isArchived ? (
                                          <>
                                              {canEdit && (
                                                  <Button
                                                      variant="plain"
                                                      color="neutral"
                                                      size="auto"
                                                      className="w-8 h-8 rounded-full hover:bg-border"
                                                      onClick={() => onEdit(c)}
                                                  >
                                                      <Pencil className="w-4 h-4 text-muted-foreground" />
                                                  </Button>
                                              )}
                                              {canDelete && (
                                                  <Button
                                                      variant="plain"
                                                      color="destructive"
                                                      size="auto"
                                                      className="w-8 h-8 rounded-full"
                                                      onClick={() => onDelete(c)}
                                                  >
                                                      <Archive className="w-4 h-4 text-destructive" />
                                                  </Button>
                                              )}
                                          </>
                                      ) : null}
                                  </div>
                              );
                          },
                          meta: { hideOnMobile: true, className: 'w-24' },
                      },
                  ]
                : []),
        ],
        [canEdit, canDelete, canReactivate, hasActions, user, onEdit, onDelete, onReactivate, t],
    );

    return (
        <div
            className="bg-card rounded-2xl overflow-hidden border border-border flex flex-col"
            style={{ height: height ? `${height}px` : 'auto' }}
        >
            {/* Header */}
            <div className="p-[20px] md:p-5 flex items-center justify-between border-b border-border flex-shrink-0">
                <div className="flex items-center gap-[12px]">
                    <div className="w-[40px] h-[40px] bg-muted rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-foreground" />
                    </div>
                    <div>
                        <h2 className="text-[14px] text-foreground">{t('common:equipe')}</h2>
                        <p className="text-[12px] text-muted-foreground">{t('common:nMembres', { count: displayCount })}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {canEditPermissions && (
                        <Button
                            variant="outline"
                            color="neutral"
                            size="xmd"
                            onClick={() => navigate('/settings/permissions')}
                        >
                            <Shield className="h-5 w-5" />
                            <span className="desktop-only hidden md:inline">{t('common:autorisations')}</span>
                        </Button>
                    )}
                    {canInvite && (
                        <Button variant="solid" color="primary" size="xmd" onClick={onInvite}>
                            <Plus className="h-5 w-5 text-primary-foreground" />
                            <span className="desktop-only hidden md:inline">{t('common:ajouter')}</span>
                        </Button>
                    )}
                </div>
            </div>

            {/* Search bar (server-side pagination or > 10 collaborators) */}
            {(manualPagination || collaborators.length > 10) && (
                <div className="px-5 py-3 flex-shrink-0 border-b border-border">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder={t('common:rechercherUnCollaborateur')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-10 pr-10 py-2.5 bg-muted border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary"
                        />
                        {search && (
                            <Button
                                type="button"
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={() => setSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 h-auto w-auto rounded-full hover:bg-border transition-colors"
                            >
                                <X className="w-4 h-4 text-muted-foreground" />
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Desktop DataTable */}
            <div className="hidden md:flex md:flex-col flex-1 min-h-0 overflow-hidden">
                <div className="flex-1 min-h-0 overflow-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:bg-card [&_thead]:z-10">
                    <DataTable
                        columns={columns}
                        data={filteredCollaborators}
                        emptyMessage={t('common:aucunCollaborateurTrouve')}
                        showPagination={false}
                        manualPagination={manualPagination}
                        pageCount={pageCount}
                        currentPage={currentPage}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        isLoading={isLoading}
                    />
                </div>
                {manualPagination && filteredCollaborators.length > 0 && (
                    <DataTablePagination
                        table={{
                            getState: () => ({
                                pagination: { pageIndex: (currentPage ?? 1) - 1, pageSize: pageSize ?? 10 },
                            }),
                            getPageCount: () => pageCount ?? 1,
                            getCanPreviousPage: () => (currentPage ?? 1) > 1,
                            getCanNextPage: () => (currentPage ?? 1) < (pageCount ?? 1),
                            setPageIndex: (idx) => onPageChange?.(idx + 1),
                            previousPage: () => onPageChange?.((currentPage ?? 1) - 1),
                            nextPage: () => onPageChange?.((currentPage ?? 1) + 1),
                            setPageSize: (size) => onPageSizeChange?.(size),
                            getFilteredRowModel: () => ({ rows: { length: totalCount ?? 0 } }),
                        }}
                        totalCount={totalCount}
                        manualPagination={true}
                    />
                )}
            </div>

            {/* Mobile Cards */}
            <div className="mobile-only md:hidden flex-1 min-h-0 overflow-auto p-3">
                <div className="space-y-2">
                    {filteredCollaborators.map((c) => {
                        const isCurrentUser = user && c.email === user.email;
                        const isArchived = c.status === 'Compte archivé' || c.status === 'ARCHIVED';
                        return (
                            <div key={c.id} className="p-4 bg-muted rounded-xl">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-[15px] text-foreground font-medium">{c.name || '—'}</h3>
                                        <p className="text-[13px] text-muted-foreground truncate">{c.email}</p>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            {c.role &&
                                                (() => {
                                                    const roleName = typeof c.role === 'string' ? c.role : c.role?.name;
                                                    const displayName =
                                                        typeof c.role === 'string'
                                                            ? getRoleLabel(c.role)
                                                            : (c.role?.display_name ?? getRoleLabel(roleName));
                                                    return (
                                                        <span
                                                            className={`inline-flex items-center px-2 py-[3px] rounded-md text-[12px] ${getRoleBadgeClass(roleName)}`}
                                                        >
                                                            {displayName}
                                                        </span>
                                                    );
                                                })()}
                                            {isArchived && (
                                                <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[12px] bg-muted text-muted-foreground border border-border">
                                                    <Archive className="w-3 h-3" />
                                                    {t('common:archive')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {!isCurrentUser && hasActions && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {isArchived && canReactivate ? (
                                                <Button
                                                    variant="plain"
                                                    color="neutral"
                                                    size="auto"
                                                    className="w-8 h-8 rounded-full hover:bg-border"
                                                    onClick={() => onReactivate(c)}
                                                    title={t('common:restaurer')}
                                                >
                                                    <RotateCcw className="w-4 h-4 text-muted-foreground" />
                                                </Button>
                                            ) : !isArchived ? (
                                                <>
                                                    {canEdit && (
                                                        <Button
                                                            variant="plain"
                                                            color="neutral"
                                                            size="auto"
                                                            className="w-8 h-8 rounded-full hover:bg-border"
                                                            onClick={() => onEdit(c)}
                                                        >
                                                            <Pencil className="w-4 h-4 text-muted-foreground" />
                                                        </Button>
                                                    )}
                                                    {canDelete && (
                                                        <Button
                                                            variant="plain"
                                                            color="destructive"
                                                            size="auto"
                                                            className="w-8 h-8 rounded-full"
                                                            onClick={() => onDelete(c)}
                                                        >
                                                            <Archive className="w-4 h-4 text-destructive" />
                                                        </Button>
                                                    )}
                                                </>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {filteredCollaborators.length === 0 && (
                        <div className="p-8 text-center text-muted-foreground text-sm">{t('common:aucunCollaborateurTrouve')}</div>
                    )}
                </div>
                {manualPagination && filteredCollaborators.length > 0 && (
                    <DataTablePagination
                        table={{
                            getState: () => ({
                                pagination: { pageIndex: (currentPage ?? 1) - 1, pageSize: pageSize ?? 10 },
                            }),
                            getPageCount: () => pageCount ?? 1,
                            getCanPreviousPage: () => (currentPage ?? 1) > 1,
                            getCanNextPage: () => (currentPage ?? 1) < (pageCount ?? 1),
                            setPageIndex: (idx) => onPageChange?.(idx + 1),
                            previousPage: () => onPageChange?.((currentPage ?? 1) - 1),
                            nextPage: () => onPageChange?.((currentPage ?? 1) + 1),
                            setPageSize: (size) => onPageSizeChange?.(size),
                            getFilteredRowModel: () => ({ rows: { length: totalCount ?? 0 } }),
                        }}
                        totalCount={totalCount}
                        manualPagination={true}
                    />
                )}
            </div>
        </div>
    );
}
